import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { BroadcastIcon, UsersThreeIcon, CalendarBlankIcon, HeartIcon } from "@phosphor-icons/react";
import TrustTierBadge from "@/components/TrustTierBadge";
import BookmarkButton from "@/components/BookmarkButton";
import SellerAvatar from "@/components/SellerAvatar";
import ComingSoonBanner from "@/components/ComingSoonBanner";
import HotLotBadge from "@/components/HotLotBadge";
import MomentumCarousel from "@/components/momentum/MomentumCarousel";
import SpotlightConstellation from "@/components/beacon/SpotlightConstellation";

export default function Live() {
  const { user } = useAuth();
  const [live, setLive] = useState([]);
  const [scheduled, setScheduled] = useState([]);
  const [followingOnly, setFollowingOnly] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState(null);
  const [categories, setCategories] = useState([]);

  const refresh = () => {
    const params = {};
    if (followingOnly) params.following = true;
    if (categoryFilter) params.category = categoryFilter;
    api
      .get("/streams", { params: { status: "live", ...params } })
      .then((r) => setLive(r.data.items));
    api
      .get("/streams", { params: { status: "scheduled", ...params } })
      .then((r) => setScheduled(r.data.items));
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [followingOnly, categoryFilter]);

  useEffect(() => {
    api.get("/categories")
      .then((r) => setCategories(r.data.categories || []))
      .catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="live-index-page">
      <Navbar />
      <ComingSoonBanner />
      {/* Premium 3D coverflow of the hottest shows. Mounted full-width
          at the top so visitors get the highest-momentum streams in
          their face before the filter chips and category bar. */}
      <MomentumCarousel />
      <main className="flex-1 max-w-7xl mx-auto w-full px-6 py-10 space-y-14">
        {/* LIVE */}
        <section>
          <div className="flex items-end justify-between gap-4 flex-wrap">
            <h1 className="font-display font-black text-4xl lg:text-5xl tracking-tighter">
              Real time streams
            </h1>
            {user && (
              <div className="flex items-center gap-2" data-testid="live-filters">
                <FilterChip
                  active={!followingOnly}
                  onClick={() => setFollowingOnly(false)}
                  testId="filter-all"
                >
                  All
                </FilterChip>
                <FilterChip
                  active={followingOnly}
                  onClick={() => setFollowingOnly(true)}
                  testId="filter-following"
                  icon={<HeartIcon size={13} weight={followingOnly ? "fill" : "regular"} />}
                >
                  Following
                </FilterChip>
              </div>
            )}
          </div>

          {/* Category pill row — scrolls horizontally on mobile, wraps on
              desktop. Tapping a pill filters both LIVE + SCHEDULED to
              only shows in that category. "All" clears. */}
          {categories.length > 0 && (
            <div
              className="mt-5 flex gap-2 overflow-x-auto no-scrollbar md:flex-wrap md:overflow-visible pb-1"
              data-testid="live-category-filters"
            >
              <FilterChip
                active={!categoryFilter}
                onClick={() => setCategoryFilter(null)}
                testId="filter-cat-all"
              >
                All categories
              </FilterChip>
              {categories.map((c) => (
                <FilterChip
                  key={c.slug || c}
                  active={categoryFilter === (c.slug || c)}
                  onClick={() => setCategoryFilter(c.slug || c)}
                  testId={`filter-cat-${c.slug || c}`}
                >
                  {c.name || c}
                </FilterChip>
              ))}
            </div>
          )}
          {live.length === 0 ? (
            <div className="mt-8 space-y-6">
              {/* The chosen-few constellation takes over the empty state.
                  Even when no one's live, the spotlight sellers keep
                  the page alive. */}
              <SpotlightConstellation />
              <div className="card-modern p-10 text-center">
                <BroadcastIcon size={28} className="mx-auto text-zinc-300" />
                <p className="font-display font-bold text-xl mt-3">
                  {followingOnly
                    ? "Nobody you follow is live right now."
                    : "No one's live right now."}
                </p>
                <p className="text-sm text-zinc-600 mt-1">
                  {followingOnly
                    ? "Tap All to see every show happening on Bidorah."
                    : "Check back soon — or go live yourself."}
                </p>
              </div>
            </div>
          ) : (
            <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
              {live.map((s) => (
                <StreamCard key={s.id} stream={s} onChange={refresh} />
              ))}
            </div>
          )}
        </section>

        {/* UPCOMING / SCHEDULED */}
        {scheduled.length > 0 && (
          <section data-testid="scheduled-section">
            <div className="flex items-center gap-2 mb-2">
              <CalendarBlankIcon size={18} className="text-amber-600" />
              <p className="text-sm font-bold text-amber-700">Upcoming · Bookmark to get pinged</p>
            </div>
            <h2 className="font-display font-black text-3xl lg:text-4xl tracking-tighter">
              Shows starting soon
            </h2>
            <p className="text-sm text-zinc-600 mt-1">
              Bookmark a show and we'll notify you the second it goes live.
            </p>
            <div className="mt-6 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
              {scheduled.map((s) => (
                <StreamCard key={s.id} stream={s} onChange={refresh} upcoming />
              ))}
            </div>
          </section>
        )}
      </main>
      <Footer />
    </div>
  );
}

function StreamCard({ stream: s, onChange, upcoming = false }) {
  return (
    <div
      className={`group card-modern hover-lift ${upcoming ? "ring-1 ring-amber-200" : ""}`}
      data-testid={`${upcoming ? "scheduled" : "live"}-card-${s.id}`}
    >
      <Link to={`/stream/${s.id}`} className="block">
        <div className="aspect-video bg-zinc-900 relative grid place-items-center text-white overflow-hidden">
          <BroadcastIcon size={48} className="text-zinc-600" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
          {upcoming ? (
            <div className="absolute top-3 left-3 pill pill-scheduled">
              <CalendarBlankIcon size={10} /> SCHEDULED
            </div>
          ) : (
            <div className="absolute top-3 left-3 pill pill-live">
              <span className="h-1.5 w-1.5 rounded-full bg-white live-dot" /> LIVE
            </div>
          )}
          {s.is_sponsored && (
            <div
              className="absolute top-3 left-20 pill bg-amber-400 text-amber-950 font-black"
              data-testid={`sponsored-stream-${s.id}`}
            >
              SPONSORED
            </div>
          )}
          {!upcoming && (
            <div className="absolute top-3 right-3 pill pill-dark">
              <UsersThreeIcon size={10} /> {s.viewer_count || 0}
            </div>
          )}
          {!upcoming && (s.heat_level === "hot" || s.heat_level === "frenzy") && (
            <div
              className="absolute top-3 right-20"
              data-testid={`stream-card-heat-${s.id}`}
            >
              <HotLotBadge level={s.heat_level} compact />
            </div>
          )}
          {upcoming && s.scheduled_at && (
            <div className="absolute bottom-3 right-3 pill pill-dark">
              {new Date(s.scheduled_at).toLocaleString(undefined, {
                month: "short",
                day: "numeric",
                hour: "numeric",
                minute: "2-digit",
              })}
            </div>
          )}
          {/* Seller profile circle overlapping bottom-left of thumbnail */}
          <div className="absolute -bottom-5 left-4 z-10">
            <SellerAvatar
              name={s.seller_name}
              picture={s.seller_picture}
              size={44}
            />
          </div>
        </div>
      </Link>
      <div className="p-4 pt-5 pl-[72px]">
        <div className="flex items-center gap-2 flex-wrap mb-2">
          <span
            className={`pill ${
              s.segment === "wholesale" ? "bg-amber-500 text-white" : "bg-[#1D78AB] text-white"
            }`}
          >
            {s.segment}
          </span>
          {s.seller_trust_tier && (
            <TrustTierBadge tier={s.seller_trust_tier} size="xs" withIcon={false} />
          )}
          {s.age_restricted && (
            <span
              className="pill bg-rose-600 text-white font-black tracking-widest"
              data-testid={`stream-card-18plus-${s.id}`}
              title="Adults only — viewers must confirm 18+ to enter"
            >
              18+
            </span>
          )}
        </div>
        <Link to={`/stream/${s.id}`}>
          <div className="font-display font-bold text-lg line-clamp-1 group-hover:text-[#1D78AB] transition-colors">
            {s.title}
          </div>
        </Link>
        <div className="flex items-center justify-between mt-2">
          <div className="text-xs text-zinc-500 truncate flex-1">{s.seller_name} · {s.category}</div>
          <BookmarkButton
            streamId={s.id}
            initialBookmarked={s.is_bookmarked}
            initialCount={s.bookmark_count}
            size="sm"
            onChange={onChange}
          />
        </div>
      </div>
    </div>
  );
}


function FilterChip({ active, onClick, icon, testId, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-xs font-bold tracking-wide border transition-colors shrink-0 whitespace-nowrap ${
        active
          ? "bg-[#1D78AB] text-white border-[#1D78AB] shadow-sm"
          : "bg-white text-zinc-700 border-zinc-300 hover:border-[#1D78AB] hover:text-[#1D78AB]"
      }`}
      data-testid={testId}
      aria-pressed={active}
    >
      {icon}
      {children}
    </button>
  );
}
